'use client'

import { IntlErrorCode } from '@formatjs/intl'
import { type FormatXMLElementFn } from 'intl-messageformat'
import { type FC, type PropsWithChildren, type ReactNode } from 'react'
import { type IntlConfig, IntlProvider } from 'react-intl'

import { apmCaptureError } from '../../../apm'
import { type Messages } from '../../i18nTypes'
import { type LOCALE } from '../../LOCALE'
import { I18nBaseUiLocaleContext } from '../i18nBaseUiLocaleContext'

import { customElements } from './customElements'
import { htmlElements } from './htmlElements'

const defaultRichTextElements: Record<string, FormatXMLElementFn<ReactNode>> = {
  ...htmlElements,
  ...customElements,
}

/* eslint-disable no-console -- log useful information during development */
const onError: IntlConfig['onError'] = (error) => {
  if (process.env.NODE_ENV === 'production') {
    apmCaptureError(error)
  } else if (error.code === IntlErrorCode.MISSING_TRANSLATION) {
    console.warn(
      `${error.message}\nRun "yarn install && yarn workspace app run compile-translations" to update locale files.\n`,
    )
  } else {
    console.warn(error)
  }
}
/* eslint-enable no-console */

type LocaleProviderProps = PropsWithChildren<{
  locale: LOCALE
  localeMessages: Messages
  timeZone: string
}>

export const I18nProvider: FC<LocaleProviderProps> = ({
  children,
  locale,
  localeMessages,
  timeZone,
}) => (
  <IntlProvider
    defaultRichTextElements={defaultRichTextElements}
    locale={locale}
    messages={localeMessages}
    onError={onError}
    timeZone={timeZone}
  >
    <I18nBaseUiLocaleContext>{children}</I18nBaseUiLocaleContext>
  </IntlProvider>
)
